// ABOUTME: Modal component for creating and editing job roles
// ABOUTME: Extracted from ManageRoles to keep the main component focused on listing

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useJobRoles, JobRoleWithStats } from '@/hooks/useJobRoles';
import { useToast } from '@/hooks/use-toast';
import { Save } from 'lucide-react';
interface RoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingRole?: JobRoleWithStats | null;
}
const RoleFormModal = ({
  isOpen,
  onClose,
  editingRole
}: RoleFormModalProps) => {
  const {
    createJobRole,
    updateJobRole
  } = useJobRoles();
  const {
    toast
  } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    Company: '', // Maps to Company column
    department: '',
    location: '',
    salary_min: '',
    salary_max: '',
    description: '',
    bonus_amount: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    requirements: '',
    Essential_Skills: '', // Maps to Essential_Skills column
    Key_deliverables: '', // Maps to Key_deliverables column
    required_tools: '', // Will be stored as array
    reports_to: '',
    experience: '',
    is_active: true
  });
  useEffect(() => {
    if (editingRole) {
      setFormData({
        title: editingRole.title,
        Company: (editingRole as any).Company || '',
        department: editingRole.department,
        location: editingRole.location,
        salary_min: editingRole.salary_min?.toString() || '',
        salary_max: editingRole.salary_max?.toString() || '',
        description: editingRole.description || '',
        bonus_amount: editingRole.bonus_amount.toString(),
        priority: editingRole.priority || 'medium',
        requirements: editingRole.requirements || '',
        Essential_Skills: (editingRole as any).Essential_Skills || '',
        Key_deliverables: (editingRole as any).Key_deliverables || '',
        required_tools: Array.isArray(editingRole.required_tools) ? editingRole.required_tools.join(', ') : (editingRole.required_tools || ''),
        reports_to: (editingRole as any).reports_to || '',
        experience: (editingRole as any).experience || '',
        is_active: editingRole.is_active ?? true
      });
    } else {
      setFormData({
        title: '',
        Company: '',
        department: '',
        location: '',
        salary_min: '',
        salary_max: '',
        description: '',
        bonus_amount: '',
        priority: 'medium',
        requirements: '',
        Essential_Skills: '',
        Key_deliverables: '',
        required_tools: '',
        reports_to: '',
        experience: '',
        is_active: true
      });
    }
  }, [editingRole, isOpen]);
  const handleSaveRole = async () => {
    try {
      // Convert required_tools string to array for database storage
      const toolsArray = formData.required_tools 
        ? formData.required_tools.split(',').map(tool => tool.trim()).filter(tool => tool.length > 0)
        : [];

      const roleData = {
        title: formData.title,
        Company: formData.Company.trim() || null,
        department: formData.department,
        location: formData.location,
        salary_min: formData.salary_min || null,
        salary_max: formData.salary_max || null,
        description: formData.description.trim() || null,
        bonus_amount: formData.bonus_amount.toString(),
        priority: formData.priority,
        requirements: formData.requirements.trim() || null,
        Essential_Skills: formData.Essential_Skills.trim() || null,
        Key_deliverables: formData.Key_deliverables.trim() || null,
        required_tools: toolsArray.length > 0 ? toolsArray : null,
        reports_to: formData.reports_to.trim() || null,
        experience: formData.experience.trim() || null,
        is_active: formData.is_active
      };
      if (editingRole) {
        await updateJobRole.mutateAsync({
          id: editingRole.id,
          ...roleData
        });
        toast({
          title: "Role updated successfully"
        });
      } else {
        await createJobRole.mutateAsync(roleData);
        toast({
          title: "Role created successfully"
        });
      }
      onClose();
    } catch (error) {
      toast({
        title: "Error saving role",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };
  return <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Job Title</Label>
              <Input id="title" value={formData.title} onChange={e => setFormData(prev => ({
              ...prev,
              title: e.target.value
            }))} placeholder="Senior Software Engineer" />
            </div>
            <div>
              <Label htmlFor="Company">Company Name</Label>
              <Input id="Company" value={formData.Company} onChange={e => setFormData(prev => ({
              ...prev,
              Company: e.target.value
            }))} placeholder="Acme Corp" />
            </div>
            <div>
              <Label htmlFor="department">Department</Label>
              <Input id="department" value={formData.department} onChange={e => setFormData(prev => ({
              ...prev,
              department: e.target.value
            }))} placeholder="Engineering" />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input id="location" value={formData.location} onChange={e => setFormData(prev => ({
              ...prev,
              location: e.target.value
            }))} placeholder="San Francisco, CA" />
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value: 'low' | 'medium' | 'high') => setFormData(prev => ({
              ...prev,
              priority: value
            }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bonus_amount">Performance Incentive ($)</Label>
              <Input id="bonus_amount" type="number" value={formData.bonus_amount} onChange={e => setFormData(prev => ({
              ...prev,
              bonus_amount: e.target.value
            }))} placeholder="5000" />
            </div>
            <div>
              <Label htmlFor="salary_min">Minimum Salary ($)</Label>
              <Input id="salary_min" type="number" value={formData.salary_min} onChange={e => setFormData(prev => ({
              ...prev,
              salary_min: e.target.value
            }))} placeholder="120000" />
            </div>
            <div>
              <Label htmlFor="salary_max">Maximum Salary ($)</Label>
              <Input id="salary_max" type="number" value={formData.salary_max} onChange={e => setFormData(prev => ({
              ...prev,
              salary_max: e.target.value
            }))} placeholder="180000" />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Job Description</Label>
            <Textarea id="description" value={formData.description} onChange={e => setFormData(prev => ({
            ...prev,
            description: e.target.value
          }))} placeholder="Describe the role and responsibilities..." rows={4} />
          </div>

          <div>
            <Label htmlFor="requirements">Requirements</Label>
            <Textarea id="requirements" value={formData.requirements} onChange={e => setFormData(prev => ({
            ...prev,
            requirements: e.target.value
          }))} placeholder="List the job requirements here (one per line or as a paragraph)..." rows={4} />
          </div>

          <div>
            <Label htmlFor="Essential_Skills">Essential Skills</Label>
            <Textarea id="Essential_Skills" value={formData.Essential_Skills} onChange={e => setFormData(prev => ({
            ...prev,
            Essential_Skills: e.target.value
          }))} placeholder="List the essential skills needed for this role..." rows={3} />
          </div>

          <div>
            <Label htmlFor="required_tools">Required Tools (comma-separated)</Label>
            <Textarea id="required_tools" value={formData.required_tools} onChange={e => setFormData(prev => ({
            ...prev,
            required_tools: e.target.value
          }))} placeholder="React, Node.js, TypeScript, AWS..." rows={3} />
          </div>

          <div>
            <Label htmlFor="Key_deliverables">Key Deliverables</Label>
            <Textarea id="Key_deliverables" value={formData.Key_deliverables} onChange={e => setFormData(prev => ({
            ...prev,
            Key_deliverables: e.target.value
          }))} placeholder="List the key deliverables for this role..." rows={3} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="reports_to">Reports To</Label>
              <Input id="reports_to" value={formData.reports_to} onChange={e => setFormData(prev => ({
              ...prev,
              reports_to: e.target.value
            }))} placeholder="Engineering Manager" />
            </div>
            <div>
              <Label htmlFor="experience">Experience Required</Label>
              <Input id="experience" value={formData.experience} onChange={e => setFormData(prev => ({
              ...prev,
              experience: e.target.value
            }))} placeholder="3-5 years" />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({
                ...prev,
                is_active: checked
              }))}
            />
            <Label htmlFor="is_active">
              Status: {formData.is_active ? 'Active' : 'Inactive'}
            </Label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSaveRole}>
              <Save className="w-4 h-4 mr-2" />
              Save Role
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
export default RoleFormModal;